import { Component, Host, h, Prop } from '@stencil/core';
import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
dayjs.extend(customParseFormat);

/** This is an extremely simple component which just renders a date string according to the user's browser's localization settings. So, for example, a user in the US would render October 31, 2022 as "10/31/2022" whereas a user in the UK would render the same date as "31/10/2022".
 *
 * Why even make this component at all if it is so simple? Because under the hood the component uses the javascript `Intl` date formatter functions, and while very convenient, they are also comparitively very slow to run compared to other js functions.
 *
 * By breaking into a separate component we can effectively defer the running of the `Intl.DateTimeFormat` function until after the component is fully mounted, meaning any _other_ component that uses this component instead of calling the `Intl.DateTimeFormat` function itself also runs faster. By using the `aha-date` _component_ vs. just using the `Intl.DateTimeFormat` _directly_ in our code, we effectively increase render speed.
 */
// NOTE: This performance impact was very apparent in the aha-calendar component ... if we called `Intl.DateTimeFormat` directly within `<aha-calendar>`, and then used up/down arrow keys to navigate through years, we could cause the UI to hang for as much as second as the dates quickly changed. By using a separate component, the UI remains buttery smooth.
export interface AhaDate extends HTMLAhaDateElement {}

@Component({
  tag: 'aha-date',
  shadow: true,
})
export class AhaDate {
  /** The date to be shown. Must be in format YYYY-MM-DD. */
  @Prop({ reflect: true }) date!: string;

  /** The format of the resultant date string. Short is a custom short method showing the full 4 digit year */
  @Prop({ reflect: true }) format: 'full' | 'long' | 'medium' | 'short' = 'short';

  /** The format of the resultant time string. */
  @Prop({ reflect: true }) timeFormat?: 'full' | 'long' | 'medium' | 'short';

  /** The locale to use for the date string. Defaults to the user's locale or the browser's locale. */
  @Prop({ reflect: true }) locale: string = window?.currentUser?.locale || navigator.language;

  render() {
    let options: Intl.DateTimeFormatOptions = {};

    if (this.format === 'short') {
      options = {
        month: '2-digit',
        day: '2-digit',
        year: 'numeric',
        timeStyle: this.timeFormat,
      };
    } else {
      options = {
        dateStyle: this.format,
        timeStyle: this.timeFormat,
      };
    }

    if (window.currentUser?.zone) {
      options.timeZone = window.currentUser.zone;
    }

    const dateFormat = new Intl.DateTimeFormat(this.locale, options);

    return <Host>{dateFormat.format(dayjs(this.date).toDate())}</Host>;
  }
}
